/**
 * Eric 2026-05-12 — Modale d'aide raccourcis clavier de VideoDetailPage.
 * Extraite du gros fichier VideoDetailPage.js dans le cadre du refacto P2.
 * Affichée via la touche `?` ou le bouton "Tous les raccourcis".
 */
import React from "react";

const SECTIONS = (lang) => [
  {
    title: lang === "fr" ? "Lecture" : "Playback",
    items: [
      { keys: ["Espace", "K"], label: lang === "fr" ? "Lecture / Pause" : "Play / Pause" },
      { keys: ["0"], label: lang === "fr" ? "Retour au début" : "Restart" },
    ],
  },
  {
    title: lang === "fr" ? "Navigation" : "Navigation",
    items: [
      { keys: ["←"], label: lang === "fr" ? "Reculer 10 s" : "Back 10s" },
      { keys: ["→"], label: lang === "fr" ? "Avancer 10 s" : "Forward 10s" },
      { keys: ["J"], label: lang === "fr" ? "Reculer 5 s" : "Back 5s" },
      { keys: ["L"], label: lang === "fr" ? "Avancer 5 s" : "Forward 5s" },
    ],
  },
  {
    title: lang === "fr" ? "Volume" : "Volume",
    items: [
      { keys: ["↑"], label: lang === "fr" ? "Augmenter (+10%)" : "Up (+10%)" },
      { keys: ["↓"], label: lang === "fr" ? "Baisser (-10%)" : "Down (-10%)" },
      { keys: ["M"], label: lang === "fr" ? "Couper le son" : "Mute" },
    ],
  },
  {
    title: lang === "fr" ? "Vue" : "View",
    items: [
      { keys: ["F"], label: lang === "fr" ? "Plein écran" : "Fullscreen" },
      { keys: ["P"], label: lang === "fr" ? "Picture-in-Picture" : "Picture-in-Picture" },
      { keys: ["V"], label: lang === "fr" ? "Basculer mode empilé ↔ côte-à-côte" : "Toggle stacked ↔ side-by-side" },
      { keys: ["C"], label: lang === "fr" ? "Forcer mode côte-à-côte" : "Force side-by-side" },
    ],
  },
  {
    title: lang === "fr" ? "Vitesse de lecture" : "Playback speed",
    items: [
      { keys: ["1"], label: "0.5×" },
      { keys: ["2"], label: "0.75×" },
      { keys: ["3"], label: "1× (normal)" },
      { keys: ["4"], label: "1.25×" },
      { keys: ["5"], label: "1.5×" },
      { keys: ["6"], label: "1.75×" },
      { keys: ["7"], label: "2×" },
    ],
  },
  {
    title: lang === "fr" ? "Aide" : "Help",
    items: [
      { keys: ["?"], label: lang === "fr" ? "Afficher / masquer cette aide" : "Toggle this help" },
      { keys: ["Esc"], label: lang === "fr" ? "Fermer cette fenêtre" : "Close this window" },
    ],
  },
];

export default function KeyboardShortcutsModal({ open, onClose, lang = "fr" }) {
  if (!open) return null;
  const sections = SECTIONS(lang);
  return (
    <div
      className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="shortcuts-help-modal"
    >
      <div
        className="bg-white border border-zinc-200 rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between p-5 border-b border-zinc-200">
          <h3 className="text-lg font-semibold flex items-center gap-2 text-zinc-900 font-['Outfit']">
            ⌨️ {lang === "fr" ? "Raccourcis clavier" : "Keyboard shortcuts"}
          </h3>
          <button
            onClick={onClose}
            className="text-zinc-400 hover:text-zinc-700 transition-colors"
            data-testid="close-shortcuts-help-btn"
            aria-label="Fermer"
          >
            ✕
          </button>
        </div>
        <div className="p-5 grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-5 text-sm">
          {sections.map((section) => (
            <div key={section.title} data-testid={`shortcuts-section-${section.title}`}>
              <h4 className="text-xs font-semibold uppercase tracking-wider text-zinc-500 mb-2.5">
                {section.title}
              </h4>
              <div className="space-y-2">
                {section.items.map((it, idx) => (
                  <div key={idx} className="flex items-center justify-between gap-3">
                    <span className="text-zinc-700">{it.label}</span>
                    <div className="flex items-center gap-1.5 shrink-0">
                      {it.keys.map((k, ki) => (
                        <kbd
                          key={ki}
                          className="min-w-[1.75rem] px-2 py-1 bg-zinc-100 border border-zinc-300 rounded text-zinc-700 font-mono text-[11px] text-center shadow-sm"
                        >
                          {k}
                        </kbd>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
        <div className="px-5 py-3 border-t border-zinc-200 bg-zinc-50 text-xs text-zinc-500">
          {lang === "fr"
            ? "Les raccourcis sont désactivés pendant la saisie de texte (champs, zones modifiables)."
            : "Shortcuts are disabled while typing (input fields, editable areas)."}
        </div>
      </div>
    </div>
  );
}
